import { makeObservable, observable, computed, reaction, action, runInAction } from 'mobx';
import { Disposer } from '../../custom_shared/misc';
import { WorksheetModel } from '../models';
import { WorksheetStatusFilter, WorksheetDto } from '../types';
import { SourceStore } from '../../custom_shared/stores';
import { SourceModelBase } from '../../custom_shared/models';
import { AccountingStatementService } from '../services';
import { LocalStorageService } from '../../common/services';
import SessionStore from './SessionStore';
import WorksheetsChangesStore from './WorksheetsChangesStore';

export default class WorksheetsStore extends Disposer {
    worksheets: WorksheetModel[] = [];

    worksheetStatusFilter: WorksheetStatusFilter;

    private readonly localStorageService = new LocalStorageService();

    constructor(
        private readonly sessionStore: SessionStore,
        private readonly sourceStore: SourceStore,
        private readonly worksheetsChangesStore: WorksheetsChangesStore,
        private readonly service: AccountingStatementService
    ) {
        super();

        makeObservable(this, {
            worksheets: observable,
            worksheetStatusFilter: observable,
            setWorksheetStatusFilter: action,
            filteredWorksheets: computed,
            selectedWorksheet: computed
        });

        this.worksheetStatusFilter = this.getWorksheetsStatusFilterFromLocalStorage();

        this.reactions.push(
            reaction(
                () => this.sessionStore.response,
                () => {
                    if (this.sessionStore.inProgress) {
                        return;
                    }

                    runInAction(() => {
                        const data = this.sessionStore.response?.worksheets ?? [];

                        this.worksheets = this.createOrUpdateWorksheets(data, this.sourceStore.sources);

                        if (!this.selectedWorksheet && this.filteredWorksheets.length) {
                            this.selectWorksheet(this.filteredWorksheets[0]);
                        }
                    });
                }
            )
        );
    }

    get filteredWorksheets() {
        if (this.worksheetStatusFilter === 'All') {
            return this.worksheets;
        }

        if (this.worksheetStatusFilter === 'OnlyWithChanges') {
            return this.worksheets.filter(worksheet => worksheet.hasChanges);
        }

        if (this.worksheetStatusFilter === 'HideSuccessful') {
            return this.worksheets.filter(worksheet => !worksheet.isSuccessful);
        }

        return this.worksheets.filter(worksheet => worksheet.status === this.worksheetStatusFilter);
    }

    get selectedWorksheet() {
        return this.worksheets.find(worksheet => worksheet.isSelected);
    }

    getInputByGuid(inputGuid: string) {
        for (const worksheet of this.worksheets) {
            const input = worksheet.getInputByGuid(inputGuid);

            if (input) {
                return input;
            }
        }

        return undefined;
    }

    setWorksheetStatusFilter(worksheetStatusFilter: WorksheetStatusFilter) {
        this.worksheetStatusFilter = worksheetStatusFilter;
        this.updateLocalStorageWorksheetsStatusFilter();
    }

    selectWorksheet(worksheet: WorksheetModel) {
        if (this.selectedWorksheet && this.selectedWorksheet.id !== worksheet.id) {
            this.selectedWorksheet.setIsSelected(false);
        }

        if (!worksheet.isSelected) {
            worksheet.setIsSelected(true);
        }
    }

    createOrUpdateWorksheets(data: WorksheetDto[], sources: SourceModelBase[]) {
        return data.map(worksheetDto => {
            const existingWorksheet = this.worksheets.find(w => w.id === worksheetDto.id);

            if (existingWorksheet) {
                existingWorksheet.update(worksheetDto, sources);
                return existingWorksheet;
            }

            return new WorksheetModel(
                worksheetDto,
                sources,
                this.sessionStore,
                this.worksheetsChangesStore,
                this.service
            );
        });
    }

    dispose() {
        this.disposeReactions();
    }

    private updateLocalStorageWorksheetsStatusFilter() {
        this.localStorageService.setItem(this.getWorksheetsStatusFilterKey(), this.worksheetStatusFilter);
    }

    private getWorksheetsStatusFilterFromLocalStorage() {
        return this.localStorageService.getItem<WorksheetStatusFilter>(this.getWorksheetsStatusFilterKey(), 'All');
    }

    private getWorksheetsStatusFilterKey() {
        return `worksheets-status-filter-${this.sessionStore.sessionId}`;
    }
}
